// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import type { NodeFeed } from './conductors/flowPulse';
import { ChevronDown } from '@carbon/icons-react';
import { StatusMark } from './primitives/StatusMark';
import type { StatusToken } from '../../../utils/entityHelpers';
import type { PowerFlowNode } from '../../../utils/powerFlowLayout';

export interface SldLoadGroupProps {
  node: PowerFlowNode;
  /** The worst status among the loads behind the badge. */
  status: StatusToken;
  /** Whether power is reaching it: the status dot pulses with the flow, or dims. */
  feed?: NodeFeed;
  /** Display type of the members, e.g. `IT Rack`. */
  typeLabel: string;
  /** What the members are standing over after a fold: `12 IT Racks`. */
  note?: string;
  /** Full accessible name, built by the stage. */
  accessibleName: string;
  /** On the traced path of the current selection. */
  highlighted?: boolean;
  /** Exactly one asset per rank is focusable at a time (roving tabIndex). */
  focusable: boolean;
  onExpand: (_groupId: string) => void;
}

/**
 * A run of identical terminal loads, drawn as one box with a count.
 *
 * This is the move that makes the drawing readable at all. A site's racks
 * outnumber every other asset on the one-line by an order of magnitude, they
 * are electrically identical, and drawing each one sets the pitch the whole
 * diagram is stretched over — sixty rack chips is a canvas nobody can read and
 * conductors that run the full width of it. The mock's answer is the same one:
 * it writes `RPP × 4` under the PDU rather than drawing four boxes.
 *
 * Nothing is hidden, and that is the difference between this and dropping the
 * loads. The badge says how many, it takes the worst status among them so a
 * faulted rack still reaches the eye, and clicking it fans the group back out
 * into individual chips (the page then offers a route back). A group whose
 * loads are *not* identically fed never forms in the first place — see
 * `powerFlowCollapse.ts` — so the badge never conceals a difference in how
 * something is wired.
 */
const SldLoadGroupComponent = ({
  node,
  status,
  feed,
  typeLabel,
  note,
  accessibleName,
  highlighted = false,
  focusable,
  onExpand,
}: SldLoadGroupProps) => {
  const count = node.group?.memberIds.length ?? 0;

  const classes = ['sld-group', `sld-group--${status}`];
  if (highlighted) classes.push('is-highlighted');

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      onExpand(node.id);
    }
  };

  return (
    <div
      className={classes.join(' ')}
      style={{ left: node.x, top: node.y, width: node.w, height: node.h }}
      role="gridcell"
      tabIndex={focusable ? 0 : -1}
      aria-label={accessibleName}
      aria-expanded={false}
      data-sld-node={node.id}
      data-sld-status={status}
      data-sld-group={node.id}
      data-sld-rank={node.rank}
      onClick={() => onExpand(node.id)}
      onKeyDown={handleKeyDown}
    >
      <span className="sld-group__status">
        <StatusMark status={status} feed={feed} />
      </span>
      <span className="sld-group__label">
        {typeLabel} <span className="sld-group__count">×&nbsp;{count}</span>
        {note && (
          <span className="sld-group__note" data-sld-folded>
            {' · '}
            {note}
          </span>
        )}
      </span>
      <span className="sld-group__hint" aria-hidden="true">
        <ChevronDown size={12} />
      </span>
    </div>
  );
};

export const SldLoadGroup = React.memo(SldLoadGroupComponent);
